import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { EMOTIONS } from './MoodPicker';
import { localDate, monthLabel, formatDuration } from '../utils/localDate';

const emo = (id) => EMOTIONS.find((e) => e.id === id);

export const BilanCard = ({ bilan }) => {
  if (!bilan) return null;
  const { moods, practice } = bilan;
  const top = moods?.top ? emo(moods.top) : null;
  const evoStart = moods?.evolution ? emo(moods.evolution.start) : null;
  const evoEnd = moods?.evolution ? emo(moods.evolution.end) : null;
  return (
    <div className="text-left" data-testid="bilan-card" style={{ fontFamily: "'Manrope', sans-serif" }}>
      {moods && moods.days > 0 && (
        <div className="mb-5">
          <p className="text-[11px] uppercase tracking-wider text-gray-300 mb-2">Humeurs</p>
          <p className="text-sm text-gray-600 font-light" data-testid="bilan-mood-days">
            {moods.days} {moods.days > 1 ? 'ressentis enregistrés' : 'ressenti enregistré'}
          </p>
          <div className="flex flex-wrap gap-3 mt-3" data-testid="bilan-mood-counts">
            {EMOTIONS.filter((e) => moods.counts[e.id]).map((e) => (
              <div key={e.id} className="flex flex-col items-center">
                <img src={e.img} alt={e.label} className="w-9 h-9 object-contain" />
                <span className="text-[10px] text-gray-400 mt-0.5">{moods.counts[e.id]}j</span>
              </div>
            ))}
          </div>
          {top && moods.days >= 3 && (
            <p className="text-sm text-gray-600 font-light mt-3" data-testid="bilan-mood-top">
              La plus souvent choisie&nbsp;: <span className="text-gray-700">{top.label}</span>
            </p>
          )}
          {evoStart && evoEnd && moods.days >= 3 && (
            <div className="flex items-center gap-3 mt-3" data-testid="bilan-mood-evolution">
              <div className="flex flex-col items-center">
                <img src={evoStart.img} alt={evoStart.label} className="w-8 h-8 object-contain" />
                <span className="text-[10px] text-gray-400 mt-0.5">Début du mois</span>
              </div>
              <span className="text-gray-300">→</span>
              <div className="flex flex-col items-center">
                <img src={evoEnd.img} alt={evoEnd.label} className="w-8 h-8 object-contain" />
                <span className="text-[10px] text-gray-400 mt-0.5">Fin du mois</span>
              </div>
            </div>
          )}
        </div>
      )}
      {practice && practice.completed > 0 && (
        <div>
          <p className="text-[11px] uppercase tracking-wider text-gray-300 mb-2">Pratique</p>
          <p className="text-sm text-gray-600 font-light" data-testid="bilan-practice-count">
            Méditations terminées&nbsp;: <span className="text-gray-700">{practice.completed}</span>
          </p>
          <p className="text-sm text-gray-600 font-light mt-1" data-testid="bilan-practice-time">
            Temps total&nbsp;: <span className="text-gray-700">{formatDuration(practice.total_seconds)}</span>
          </p>
          {practice.top_title && (
            <p className="text-sm text-gray-600 font-light mt-1" data-testid="bilan-practice-top">
              La plus utilisée&nbsp;: <span className="text-gray-700">{practice.top_title}</span>
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const EXCLUDED = ['/', '/login', '/register', '/verify-email', '/reset-password', '/mot-de-passe-oublie', '/admin'];

export const MonthlyBilanGate = () => {
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();
  const [bilan, setBilan] = useState(null);
  const [visible, setVisible] = useState(false);
  const checked = useRef(false);

  const excluded =
    EXCLUDED.includes(location.pathname) ||
    location.pathname.startsWith('/shared') ||
    location.pathname.startsWith('/cadeau');

  useEffect(() => {
    if (!isAuthenticated || !user || checked.current || excluded) return;
    checked.current = true;
    const today = localDate();
    const guardKey = `bilan_checked_${user.id}_${today.slice(0, 7)}`;
    if (localStorage.getItem(guardKey)) return;
    api
      .getPendingBilan(today)
      .then((r) => {
        if (r && r.bilan) {
          setBilan(r.bilan);
          setTimeout(() => setVisible(true), 800);
        } else {
          localStorage.setItem(guardKey, '1');
        }
      })
      .catch(() => {});
  }, [isAuthenticated, user, excluded]);

  const close = () => {
    setVisible(false);
    if (bilan && user) {
      localStorage.setItem(`bilan_checked_${user.id}_${localDate().slice(0, 7)}`, '1');
      api.markBilanSeen(bilan.month).catch(() => {});
    }
  };

  if (!visible || !bilan) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-5" data-testid="monthly-bilan-modal">
      <div className="absolute inset-0 bg-black/30" onClick={close} />
      <div className="relative bg-[#FAF9F6] rounded-3xl p-6 w-full max-w-sm shadow-xl border border-gray-200 max-h-[85vh] overflow-y-auto">
        <button
          onClick={close}
          data-testid="monthly-bilan-close"
          className="absolute top-3 right-3 p-1.5 rounded-full text-gray-400 hover:text-gray-600"
        >
          <X size={18} />
        </button>
        <h2
          className="text-center text-lg text-gray-800 mb-1"
          style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 600 }}
          data-testid="monthly-bilan-title"
        >
          Bilan de {monthLabel(bilan.month)}
        </h2>
        <p className="text-center text-xs text-gray-400 font-light mb-5" style={{ fontFamily: "'Manrope', sans-serif" }}>
          Tes données du mois écoulé
        </p>
        <BilanCard bilan={bilan} />
        <button
          onClick={close}
          data-testid="monthly-bilan-ok"
          className="mt-6 w-full py-2.5 rounded-full text-sm bg-[#F79AAE] text-white active:scale-95 transition-transform"
          style={{ fontFamily: "'Manrope', sans-serif" }}
        >
          Fermer
        </button>
        <p className="text-center text-[11px] text-gray-300 font-light mt-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
          Conservé dans Journal → Mes bilans mensuels
        </p>
      </div>
    </div>
  );
};
